/**
 * Global error capture for failures the React boundary cannot see.
 *
 * React error boundaries only catch errors thrown during render/lifecycle.
 * Async errors, event-handler throws, and rejected promises — the majority of
 * real-world failures in a socket/IndexedDB/WASM-crypto app — escape them.
 * These window-level handlers funnel those into the same capture pipeline.
 *
 * Also flushes any reports that were queued offline in a previous session.
 */

import { captureError } from "./capture";
import { flushQueue } from "./queue";
import { scrubText } from "./scrub";

/**
 * Serialize a non-Error, non-string promise-rejection reason into a readable
 * string so the report carries actionable detail instead of a generic label.
 *
 * SECURITY: ORION is an E2E-encrypted messenger, so object rejection reasons
 * routinely carry sensitive values — usernames, display names, chat/room/device
 * ids, short tokens / invite codes, phone numbers, decrypted message snippets.
 * The downstream `scrubText` only redacts known shapes (JWT, Bearer, email,
 * long opaque blobs) and would NOT catch any of those. So we must NEVER ship
 * raw object values into the report. For objects we extract a strict ALLOWLIST
 * of diagnostic fields (`name` / `code` / `message`) only — `message` is itself
 * run through `scrubText` — and otherwise emit a key-name-only SUMMARY
 * (`keys=[a,b,c]`); we never serialize the value graph and never fall back to
 * `JSON.stringify(reason)` or `String(reason)` for objects.
 *
 * Promises can reject with anything, including values whose getters / `toJSON`
 * / `toString` throw, so every field read and every `String()` is wrapped in
 * try/catch — this must never throw inside an error handler. The result is
 * length-capped as a final safety bound; because no raw values are emitted, the
 * cap can no longer bisect a secret such that the downstream scrub misses it.
 */
const MAX_REASON_LENGTH = 1000;

function cap(text: string): string {
  return text.length > MAX_REASON_LENGTH
    ? `${text.slice(0, MAX_REASON_LENGTH)}…[truncated]`
    : text;
}

/** Read one allowlisted field, defensively. A throwing getter yields no part. */
function readField(
  obj: Record<string, unknown>,
  key: "name" | "code" | "message",
): string | null {
  try {
    const value = obj[key];
    if (value === undefined || value === null || value === "") {
      return null;
    }
    // Only accept PRIMITIVE values for allowlisted fields. An object value here
    // would route through its own `toString`/`valueOf`/`Symbol.toPrimitive`,
    // which could surface arbitrary (unscrubbed, for name/code) sensitive data.
    // Diagnostic identifiers are strings/numbers (DOMException.name,
    // error.code); a non-primitive is not a value we should emit.
    if (typeof value === "object" || typeof value === "function") {
      return null;
    }
    // String() on a primitive (string/number/boolean/bigint/symbol) is safe and
    // invokes no user-defined coercion that could leak an object graph.
    const text = String(value);
    // `message` can carry free text (and thus PII) — scrub it like any other
    // outgoing free-text field. `name`/`code` are diagnostic identifiers.
    const out = key === "message" ? scrubText(text) : text;
    return out ? `${key}=${out}` : null;
  } catch {
    return null;
  }
}

/** Key-name-only summary of an object — never includes any values. */
function keysSummary(obj: object): string | null {
  try {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null;
    }
    return `keys=[${keys.join(",")}]`;
  } catch {
    return null;
  }
}

function serializeReason(reason: unknown): string {
  return cap(serializeReasonRaw(reason));
}

function serializeReasonRaw(reason: unknown): string {
  if (reason === null) {
    return "null";
  }
  if (reason === undefined) {
    return "undefined";
  }
  // Functions are handled alongside objects: `String(fn)` would dump the
  // function's SOURCE TEXT, which can include closed-over literals. Treat them
  // like objects — allowlisted fields (if any) plus a key-names-only summary.
  if (typeof reason === "object" || typeof reason === "function") {
    const obj = reason as Record<string, unknown>;
    const parts: string[] = [];
    for (const key of ["name", "code", "message"] as const) {
      const part = readField(obj, key);
      if (part !== null) {
        parts.push(part);
      }
    }
    if (parts.length > 0) {
      return parts.join(" ");
    }
    // No allowlisted fields: emit key NAMES only — never any values. A bare
    // function has no own enumerable keys, so this yields the generic label.
    return keysSummary(obj) ?? "unserializable rejection reason";
  }
  // Primitives only (string is handled by the caller's fast-path). String()
  // on a number/boolean/symbol/bigint is safe and carries no object graph.
  try {
    return String(reason);
  } catch {
    return "unserializable rejection reason";
  }
}

let installed = false;

export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      // Uncaught runtime errors: targeted at window, carry an Error object.
      if (event.error instanceof Error) {
        captureError({
          error: event.error,
          scope: "root",
          source: "window.onerror",
        });
        return;
      }
      // Resource load failures target the element and do NOT bubble (hence the
      // capture phase below). Report script/stylesheet failures — these break
      // the app, e.g. a failed JS chunk — but skip media (img/audio/video) to
      // avoid noise from benign asset 404s.
      const target = event.target;
      if (
        target instanceof HTMLScriptElement ||
        target instanceof HTMLLinkElement
      ) {
        const url =
          target instanceof HTMLScriptElement ? target.src : target.href;
        captureError({
          error: new Error(
            `Resource failed to load: <${target.tagName.toLowerCase()}> ${url}`,
          ),
          scope: "root",
          source: "window.onerror",
        });
        return;
      }
      // Scripts loaded from ANOTHER ORIGIN (browser extensions, Cloudflare
      // Turnstile, Jitsi, etc.) throw errors the browser deliberately strips of
      // all detail before dispatching this event: `event.error` is null,
      // `event.filename` is "", `event.lineno`/`colno` are 0, and
      // `event.message` collapses to a generic, browser-/locale-dependent
      // placeholder ("Script error." in Chrome/Firefox — we do NOT match on
      // that literal string, since it varies). No `event.error` AND no
      // `event.filename` is the standard, locale-independent tell for this
      // opaque signature: a genuinely informative same-origin error always
      // carries at least the filename, even on the rare path where
      // `event.error` itself is unset. Falling through to the
      // `new Error(event.message)` branch below would fabricate an Error
      // whose stack points at THIS handler and report it as a real crash, so
      // tag it with a dedicated `source` instead — filterable from crashes,
      // same as "suppressed"/"idb-open" (see reportError.ts).
      if (!event.error && !event.filename) {
        captureError({
          error: new Error(
            event.message
              ? `[opaque-cross-origin] ${event.message}`
              : "[opaque-cross-origin] cross-origin script error (no detail exposed by the browser)",
          ),
          scope: "root",
          source: "opaque-cross-origin",
        });
        return;
      }
      if (event.message) {
        captureError({
          error: new Error(event.message),
          scope: "root",
          source: "window.onerror",
        });
      }
    },
    true, // capture phase — required for non-bubbling resource load errors
  );

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const error =
        reason instanceof Error
          ? reason
          : new Error(
              typeof reason === "string"
                ? reason
                : `Unhandled promise rejection: ${serializeReason(reason)}`,
            );
      captureError({ error, scope: "root", source: "unhandledrejection" });
    },
  );

  // Retry reports that could not be delivered while offline last session,
  // and again whenever connectivity returns.
  void flushQueue();
  window.addEventListener("online", () => {
    void flushQueue();
  });
}
